import { useState, useEffect } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { StatCard } from '@/components/ui/stat-card';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Euro, TrendingUp, AlertTriangle, CheckCircle, Users, Building2, Home, FileText, RefreshCw, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, BarChart, Bar, LineChart, Line, ComposedChart, ReferenceLine } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { format, subMonths, addMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function Dashboard() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  // Month selection state
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

  const formatMonthDisplay = (yyyymm: string) => {
    const [year, month] = yyyymm.split('-');
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const currentDate = new Date(year, month - 1);
    const newDate = direction === 'prev' ? subMonths(currentDate, 1) : addMonths(currentDate, 1);
    
    // Prevent navigation to future months
    const now = new Date();
    const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const newMonth = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
    
    if (newMonth > currentMonth) {
      return; // Don't allow navigation to future months
    }
    
    setSelectedMonth(`${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`);
  };

  // Check if selected month is current month or later
  const isCurrentMonthOrLater = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const selected = new Date(year, month - 1, 1);
    const now = new Date();
    const current = new Date(now.getFullYear(), now.getMonth(), 1);
    return selected >= current;
  };

  // Generate last 12 months for dropdown
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const date = subMonths(new Date(), i);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    return { value, label: formatMonthDisplay(value) };
  });

  // Setup realtime subscriptions
  useEffect(() => {
    const channels = [
      supabase.channel('dashboard-clients')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'clients' }, () => {
          queryClient.invalidateQueries({ queryKey: ['clients-count'] });
        })
        .subscribe(),
      
      supabase.channel('dashboard-scis')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'scis' }, () => {
          queryClient.invalidateQueries({ queryKey: ['scis-count'] });
        })
        .subscribe(),
      
      supabase.channel('dashboard-properties')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'properties' }, () => {
          queryClient.invalidateQueries({ queryKey: ['properties-count'] });
        })
        .subscribe(),
      
      supabase.channel('dashboard-leases')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'leases' }, () => {
          queryClient.invalidateQueries({ queryKey: ['active-leases-count'] });
        })
        .subscribe(),
      
      supabase.channel('dashboard-rent-periods')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'rent_periods' }, () => {
          queryClient.invalidateQueries({ queryKey: ['rent-stats'] });
          queryClient.invalidateQueries({ queryKey: ['rent-evolution'] });
          queryClient.invalidateQueries({ queryKey: ['unpaid-periods'] });
        })
        .subscribe(),
      
      supabase.channel('dashboard-payments')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'payments' }, () => {
          queryClient.invalidateQueries({ queryKey: ['rent-stats'] });
          queryClient.invalidateQueries({ queryKey: ['rent-evolution'] });
          queryClient.invalidateQueries({ queryKey: ['recent-payments'] });
          queryClient.invalidateQueries({ queryKey: ['unpaid-periods'] });
        })
        .subscribe(),
      
      supabase.channel('dashboard-receipts')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'receipts' }, () => {
          queryClient.invalidateQueries({ queryKey: ['receipts-count'] });
        })
        .subscribe(),
      
      supabase.channel('dashboard-tenants')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'tenants' }, () => {
          queryClient.invalidateQueries({ queryKey: ['tenants-count'] });
        })
        .subscribe(),
    ];

    return () => {
      channels.forEach(channel => supabase.removeChannel(channel));
    };
  }, [queryClient]);

  // Fetch counts
  const { data: clientsCount = 0 } = useQuery({
    queryKey: ['clients-count'],
    queryFn: async () => {
      const { count } = await supabase.from('clients').select('*', { count: 'exact', head: true });
      return count || 0;
    },
  });

  const { data: scisCount = 0 } = useQuery({
    queryKey: ['scis-count'],
    queryFn: async () => {
      const { count } = await supabase.from('scis').select('*', { count: 'exact', head: true });
      return count || 0;
    },
  });

  const { data: propertiesCount = 0 } = useQuery({
    queryKey: ['properties-count'],
    queryFn: async () => {
      const { count } = await supabase.from('properties').select('*', { count: 'exact', head: true });
      return count || 0;
    },
  });

  const { data: activeLeasesCount = 0 } = useQuery({
    queryKey: ['active-leases-count'],
    queryFn: async () => {
      const { count } = await supabase.from('leases').select('*', { count: 'exact', head: true }).eq('status', 'active');
      return count || 0;
    },
  });

  const { data: tenantsCount = 0 } = useQuery({
    queryKey: ['tenants-count'],
    queryFn: async () => {
      const { count } = await supabase.from('tenants').select('*', { count: 'exact', head: true });
      return count || 0;
    },
  });

  // Fetch rent stats for selected month
  const { data: rentStats } = useQuery({
    queryKey: ['rent-stats', selectedMonth],
    queryFn: async () => {
      const { data } = await supabase
        .from('rent_periods')
        .select('total_due, total_paid, balance, status')
        .eq('period_yyyymm', selectedMonth);
      
      if (!data || data.length === 0) {
        return { totalDue: 0, totalPaid: 0, remaining: 0, unpaidCount: 0 };
      }

      const totalDue = data.reduce((sum, p) => sum + Number(p.total_due), 0);
      const totalPaid = data.reduce((sum, p) => sum + Number(p.total_paid), 0);
      const remaining = totalDue - totalPaid;
      const unpaidCount = data.filter(p => p.status !== 'paid').length;

      return { totalDue, totalPaid, remaining, unpaidCount };
    },
  });

  const { data: receiptsCount = 0 } = useQuery({
    queryKey: ['receipts-count', selectedMonth],
    queryFn: async () => {
      const { count } = await supabase
        .from('receipts')
        .select('id, rent_periods!inner(period_yyyymm)', { count: 'exact', head: true })
        .eq('rent_periods.period_yyyymm', selectedMonth);
      return count || 0;
    },
  });

  // Fetch unpaid periods - show pending or partially paid periods
  const { data: unpaidPeriods = [] } = useQuery({
    queryKey: ['unpaid-periods'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rent_periods')
        .select(`
          id,
          period_yyyymm,
          total_due,
          total_paid,
          balance,
          due_date,
          status,
          lease_id,
          leases(
            id,
            properties(
              name,
              address
            ),
            tenants(
              first_name,
              last_name,
              company_name,
              tenant_type
            )
          )
        `)
        .order('due_date', { ascending: true })
        .limit(20); // Get more to filter
      
      if (error) {
        console.error('Error fetching unpaid periods:', error);
        throw error;
      }
      
      // Filter to only show periods where status is not 'paid' AND total_paid < total_due
      const unpaid = (data || []).filter((period: any) => {
        const status = period.status;
        const totalPaid = Number(period.total_paid || 0);
        const totalDue = Number(period.total_due || 0);
        
        // Show if status is pending, partial, or if not fully paid
        return status !== 'paid' && totalPaid < totalDue;
      }).slice(0, 5); // Limit to 5 after filtering
      
      return unpaid;
    },
  });

  // Fetch recent payments - show rent_periods with status 'paid' that have receipts
  const { data: recentPayments = [] } = useQuery({
    queryKey: ['recent-payments'],
    queryFn: async () => {
      // Get rent_periods with status 'paid' that have receipts
      const { data: paidPeriods, error: periodsError } = await supabase
        .from('rent_periods')
        .select(`
          id,
          period_yyyymm,
          total_due,
          total_paid,
          due_date,
          status,
          lease_id,
          receipts(
            id,
            generated_at
          ),
          leases(
            id,
            properties(
              name,
              address
            ),
            tenants(
              first_name,
              last_name,
              company_name,
              tenant_type
            )
          )
        `)
        .eq('status', 'paid')
        .not('receipts', 'is', null)
        .order('due_date', { ascending: false })
        .limit(10);
      
      if (periodsError) throw periodsError;
      if (!paidPeriods || paidPeriods.length === 0) return [];

      // Deduplicate by rent_period id and return top 5
      const uniquePeriods = Array.from(
        new Map(paidPeriods.map((p: any) => [p.id, p])).values()
      )
        .sort((a: any, b: any) => {
          // Sort by receipt generated_at or due_date
          const dateA = a.receipts?.[0]?.generated_at 
            ? new Date(a.receipts[0].generated_at).getTime()
            : new Date(a.due_date).getTime();
          const dateB = b.receipts?.[0]?.generated_at
            ? new Date(b.receipts[0].generated_at).getTime()
            : new Date(b.due_date).getTime();
          return dateB - dateA; // Descending order
        })
        .slice(0, 5);

      return uniquePeriods;
    },
  });

  // Fetch rent evolution for last 12 months with margin calculation
  const { data: rentEvolution = [] } = useQuery({
    queryKey: ['rent-evolution'],
    queryFn: async () => {
      const { data } = await supabase
        .from('rent_periods')
        .select('period_yyyymm, total_due, total_paid')
        .order('period_yyyymm', { ascending: true });
      
      if (!data || data.length === 0) return [];

      // Group by month and aggregate
      const monthlyData: Record<string, { due: number; paid: number }> = {};
      
      data.forEach((period) => {
        const month = period.period_yyyymm;
        if (!monthlyData[month]) {
          monthlyData[month] = { due: 0, paid: 0 };
        }
        monthlyData[month].due += Number(period.total_due);
        monthlyData[month].paid += Number(period.total_paid);
      });

      // Convert to array and format for chart
      return Object.entries(monthlyData)
        .map(([month, values]) => {
          const [year, monthNum] = month.split('-');
          const monthNamesShort = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
          return {
            name: `${monthNamesShort[parseInt(monthNum) - 1]} ${year.slice(2)}`,
            month,
            attendu: values.due,
            encaisse: values.paid,
            marge: values.paid - (values.due * 0.3), // Simplified margin: collected - 30% costs
          };
        })
        .slice(-12);
    },
  });

  // Export functions
  const exportToCSV = async (type: 'clients' | 'scis' | 'properties' | 'tenants' | 'leases') => {
    try {
      let data: any[] = [];
      let filename = '';
      let headers: string[] = [];

      switch (type) {
        case 'clients':
          const { data: clientsData } = await supabase.from('clients').select('*');
          data = clientsData || [];
          headers = ['Nom', 'Email', 'Téléphone', 'Adresse', 'Code postal', 'Ville'];
          filename = 'clients.csv';
          data = data.map(c => [c.name, c.email, c.phone, c.address, c.postal_code, c.city]);
          break;
        case 'scis':
          const { data: scisData } = await supabase.from('scis').select('*, clients(name)');
          data = scisData || [];
          headers = ['Nom', 'Client', 'SIRET', 'Adresse', 'IBAN'];
          filename = 'scis.csv';
          data = data.map(s => [s.name, s.clients?.name, s.siret, s.address, s.iban]);
          break;
        case 'properties':
          const { data: propertiesData } = await supabase.from('properties').select('*, scis(name)');
          data = propertiesData || [];
          headers = ['Nom', 'SCI', 'Adresse', 'Type', 'Surface m²', 'Coût acquisition'];
          filename = 'biens.csv';
          data = data.map(p => [p.name, p.scis?.name, p.address, p.type, p.area_sqm, p.acquisition_cost]);
          break;
        case 'tenants':
          const { data: tenantsData } = await supabase.from('tenants').select('*, scis(name)');
          data = tenantsData || [];
          headers = ['Type', 'Société', 'Prénom', 'Nom', 'Email', 'Téléphone', 'SCI'];
          filename = 'locataires.csv';
          data = data.map(t => [t.tenant_type, t.company_name, t.first_name, t.last_name, t.email, t.phone, t.scis?.name]);
          break;
        case 'leases':
          const { data: leasesData } = await supabase.from('leases').select('*, properties(name, address), tenants(first_name, last_name, company_name)').eq('status', 'active');
          data = leasesData || [];
          headers = ['Bien', 'Locataire', 'Loyer', 'Charges', 'Date début', 'Statut'];
          filename = 'baux_actifs.csv';
          data = data.map(l => [l.properties?.name || l.properties?.address, l.tenants?.company_name || `${l.tenants?.first_name} ${l.tenants?.last_name}`, l.rent_amount, l.charges_amount, l.start_date, l.status]);
          break;
      }

      const csvContent = [headers.join(';'), ...data.map(row => row.map((cell: any) => `"${cell || ''}"`).join(';'))].join('\n');
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Export ${filename} réussi`);
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getTenantName = (tenant: any) => {
    if (!tenant) return '-';
    if (tenant.tenant_type === 'professional' && tenant.company_name) {
      return tenant.company_name;
    }
    return `${tenant.first_name || ''} ${tenant.last_name || ''}`.trim() || '-';
  };

  const percentPaid = rentStats?.totalDue ? Math.round((rentStats.totalPaid / rentStats.totalDue) * 100) : 0;

  const chartConfig = {
    attendu: {
      label: "Loyers attendus",
      color: "hsl(var(--muted-foreground))",
    },
    encaisse: {
      label: "Loyers encaissés",
      color: "hsl(var(--primary))",
    },
    marge: {
      label: "Marge nette",
      color: "hsl(var(--chart-1))",
    },
  };

  return (
    <AppLayout title="Tableau de bord" description="Vue d'ensemble de votre gestion locative">
      <div className="space-y-6">
        {/* Header with month selector */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <RefreshCw className="h-3 w-3 animate-spin" />
            <span>Mise à jour en temps réel</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigateMonth('prev')}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigateMonth('next')}
              disabled={isCurrentMonthOrLater()}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* KPIs principaux with month indicator */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div 
            onClick={() => navigate('/receipts')} 
            className="cursor-pointer hover:scale-[1.02] transition-transform"
          >
            <StatCard
              title="Loyers attendus"
              value={formatCurrency(rentStats?.totalDue || 0)}
              icon={Euro}
              description={formatMonthDisplay(selectedMonth)}
              variant="default"
            />
          </div>
          <div 
            onClick={() => navigate('/receipts?tab=sent')} 
            className="cursor-pointer hover:scale-[1.02] transition-transform"
          >
            <StatCard
              title="Loyers encaissés"
              value={formatCurrency(rentStats?.totalPaid || 0)}
              icon={TrendingUp}
              description={`${percentPaid}% du total - ${formatMonthDisplay(selectedMonth)}`}
              variant="success"
            />
          </div>
          <div 
            onClick={() => navigate('/receipts')} 
            className="cursor-pointer hover:scale-[1.02] transition-transform"
          >
            <StatCard
              title="Reste à percevoir"
              value={formatCurrency(rentStats?.remaining || 0)}
              icon={AlertTriangle}
              description={`${rentStats?.unpaidCount || 0} période(s) - ${formatMonthDisplay(selectedMonth)}`}
              variant="warning"
            />
          </div>
          <div 
            onClick={() => navigate('/receipts')} 
            className="cursor-pointer hover:scale-[1.02] transition-transform"
          >
            <StatCard
              title="Quittances générées"
              value={String(receiptsCount)}
              icon={CheckCircle}
              description={formatMonthDisplay(selectedMonth)}
              variant="success"
            />
          </div>
        </div>

        {/* Stats secondaires with export buttons */}
        <div className="grid gap-4 md:grid-cols-5">
          <div className="cursor-pointer hover:scale-[1.02] transition-transform relative group">
            <div onClick={() => navigate('/scis')}>
              <StatCard title="SCI" value={String(scisCount)} icon={Building2} />
            </div>
            <Button 
              size="sm" 
              variant="ghost" 
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => { e.stopPropagation(); exportToCSV('scis'); }}
            >
              <Download className="h-3 w-3" />
            </Button>
          </div>
          <div className="cursor-pointer hover:scale-[1.02] transition-transform relative group">
            <div onClick={() => navigate('/properties')}>
              <StatCard title="Biens" value={String(propertiesCount)} icon={Home} />
            </div>
            <Button 
              size="sm" 
              variant="ghost" 
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => { e.stopPropagation(); exportToCSV('properties'); }}
            >
              <Download className="h-3 w-3" />
            </Button>
          </div>
          <div className="cursor-pointer hover:scale-[1.02] transition-transform relative group">
            <div onClick={() => navigate('/tenants')}>
              <StatCard title="Locataires" value={String(tenantsCount)} icon={Users} />
            </div>
            <Button 
              size="sm" 
              variant="ghost" 
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => { e.stopPropagation(); exportToCSV('tenants'); }}
            >
              <Download className="h-3 w-3" />
            </Button>
          </div>
          <div className="cursor-pointer hover:scale-[1.02] transition-transform relative group">
            <div onClick={() => navigate('/contracts')}>
              <StatCard title="Baux actifs" value={String(activeLeasesCount)} icon={FileText} />
            </div>
            <Button 
              size="sm" 
              variant="ghost" 
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => { e.stopPropagation(); exportToCSV('leases'); }}
            >
              <Download className="h-3 w-3" />
            </Button>
          </div>
          <div className="cursor-pointer hover:scale-[1.02] transition-transform relative group">
            <div onClick={() => navigate('/scis')}>
              <StatCard title="Clients" value={String(clientsCount)} icon={Users} />
            </div>
            <Button 
              size="sm" 
              variant="ghost" 
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => { e.stopPropagation(); exportToCSV('clients'); }}
            >
              <Download className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {/* Graphique évolution des loyers */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Évolution des loyers (12 derniers mois)</CardTitle>
          </CardHeader>
          <CardContent>
            {rentEvolution.length > 0 ? (
              <ChartContainer config={chartConfig} className="h-[350px] w-full">
                <ComposedChart data={rentEvolution} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorAttendu" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--muted-foreground))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--muted-foreground))" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorEncaisse" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                  <YAxis 
                    className="text-xs" 
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(value) => {
                      if (value >= 1000) {
                        return `${(value / 1000).toFixed(1)}k€`;
                      }
                      return `${value}€`;
                    }}
                    domain={['auto', 'auto']}
                    allowDecimals={false}
                  />
                  <ChartTooltip 
                    content={<ChartTooltipContent formatter={(value) => formatCurrency(Number(value))} />} 
                  />
                  <Area 
                    type="monotone" 
                    dataKey="attendu" 
                    stroke="hsl(var(--muted-foreground))" 
                    fillOpacity={1} 
                    fill="url(#colorAttendu)" 
                    strokeWidth={2}
                    connectNulls={false}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="encaisse" 
                    stroke="hsl(var(--primary))" 
                    fillOpacity={1} 
                    fill="url(#colorEncaisse)" 
                    strokeWidth={2}
                    connectNulls={false}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="marge" 
                    stroke="hsl(var(--chart-1))" 
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--chart-1))', r: 4 }}
                    activeDot={{ r: 6 }}
                    connectNulls={false}
                  />
                </ComposedChart>
              </ChartContainer>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Aucune donnée de loyer disponible</p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Impayés */}
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/receipts')}>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                Impayés et retards
                {unpaidPeriods.length > 0 && (
                  <Badge variant="destructive">{unpaidPeriods.length}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {unpaidPeriods.length > 0 ? (
                <div className="space-y-3">
                  {unpaidPeriods.map((period: any) => (
                    <div key={period.id} className="flex items-center justify-between p-3 bg-destructive/10 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">
                          {getTenantName(period.leases?.tenants)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {period.leases?.properties?.name || period.leases?.properties?.address}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Échéance: {format(new Date(period.due_date), 'dd MMM yyyy', { locale: fr })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-destructive">
                          {formatCurrency(period.balance)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          sur {formatCurrency(period.total_due)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50 text-green-500" />
                  <p>Aucun impayé</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Paiements récents */}
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/receipts?tab=sent')}>
            <CardHeader>
              <CardTitle className="text-lg">Derniers paiements</CardTitle>
            </CardHeader>
            <CardContent>
              {recentPayments.length > 0 ? (
                <div className="space-y-3">
                  {recentPayments.map((period: any) => {
                    const receiptDate = period.receipts?.[0]?.generated_at || period.due_date;
                    return (
                      <div key={period.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">
                            {getTenantName(period.leases?.tenants)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            {period.leases?.properties?.name || period.leases?.properties?.address}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            {format(new Date(receiptDate), 'dd MMM yyyy', { locale: fr })}
                          </p>
                        </div>
                        <p className="font-semibold text-green-600">
                          +{formatCurrency(period.total_paid || period.total_due)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Euro className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun paiement récent</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
